import React, { Suspense, lazy, useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { UserLocation } from "./firebase/types";
import LegacyAdminBookingDetailRedirect from "@/components/Site/features/admin/LegacyAdminBookingDetailRedirect";
import { SpeciesProvider } from "./contexts/SpeciesContext";
import DeferredAnalytics from "./components/DeferredAnalytics";
import UnverifiedEmailBanner from "./components/Auth/UnverifiedEmailBanner";
import { parseUserLocationMapPath, userLocationMapDetailPath } from "./utils/location";
import { dispatchMapEvent, MAP_EVENTS, subscribeMapEvent } from "./utils/map/mapEventBus";
import { hasPendingAuthRedirectFlag } from "./firebase/redirectFlags";
import {
  LEGACY_MAP_SPECIES_PATH_PREFIX,
  LEGACY_PUBLIC_SPECIES_PATH_PREFIX,
  MAP_FIELD_GUIDE_BASE,
  PUBLIC_FIELD_GUIDE_BASE,
} from "./constants/fieldGuideRoutes";
import { useEventsFeature } from "./contexts/EventsFeatureContext";
import { GUIDE_DIRECTORY_PUBLIC } from "./constants/publicSiteFocus";
import {
  DashboardEventsRouteGate,
  PublicEventsRouteGate,
} from "./routes/EventsFeatureGates";
import { PublicForecastRouteGate } from "./routes/ForecastFeatureGates";
import { useRouteSeoPolicy } from "./hooks/useRouteSeoPolicy";
import {
  FALL_COLOR_MAP_PATH,
  FALL_COLOR_SHARE_ALIAS_PATHS,
  FALL_COLOR_SHARE_PATH,
} from "./utils/map/fallColorShareUrl";

const LandingPage = lazy(() =>
  import("./components/Site/features/landing/LandingPage"),
);
const MapRouteGate = lazy(() => import("./components/Map/MapRouteGate"));
const LoginForm = lazy(() => import("./components/Auth/LoginForm"));
const MobileAuthHandoff = lazy(
  () => import("./components/Auth/MobileAuthHandoff"),
);
const AuthActionPage = lazy(() => import("./components/Auth/AuthActionPage"));
const VerifyEmailPage = lazy(() => import("./components/Auth/VerifyEmailPage"));
const UserSettings = lazy(() => import("./components/User/UserSettings"));
const HostingPage = lazy(
  () => import("./components/Site/features/hosting/HostingPage"),
);
const MembershipPage = lazy(
  () => import("./components/Site/features/membership/MembershipPage"),
);
const UserNotificationsPage = lazy(
  () => import("./components/Site/features/user/UserNotificationsPage"),
);
const SpeciesPage = lazy(
  () => import("./components/Site/features/species/pages/SpeciesPage"),
);
const SpeciesDetailPageRoute = lazy(
  () =>
    import("./components/Site/features/species/pages/SpeciesDetailPageRoute"),
);
const EventsPage = lazy(() =>
  import("./components/Site/features/events/EventsPage"),
);
const EventCategoryPage = lazy(() =>
  import("./components/Site/features/events/EventCategoryPage"),
);
const HostOnboardingPage = lazy(
  () => import("./components/Site/features/host/onboarding/HostOnboardingPage"),
);
const EventDetailPage = lazy(
  () => import("./components/Site/features/events/EventDetailPage"),
);
const EventCheckoutPage = lazy(
  () => import("./components/Site/features/events/EventCheckoutPage"),
);
const GuideDirectoryDetailPage = lazy(
  () => import("./components/Site/features/events/GuideDirectoryDetailPage"),
);
const GuideSearchResultsPage = lazy(
  () => import("./components/Site/features/events/GuideSearchResultsPage"),
);
const HostPublicProfilePage = lazy(
  () => import("./components/Site/features/host/profile/HostPublicProfilePage"),
);
const BookingConfirmedPage = lazy(
  () => import("./components/Site/features/booking/BookingConfirmedPage"),
);
const PrivacyPolicy = lazy(
  () => import("./components/Site/features/legal/PrivacyPolicy"),
);
const TermsOfService = lazy(
  () => import("./components/Site/features/legal/TermsOfService"),
);
const NotFoundPage = lazy(
  () => import("./components/Site/features/notFound/NotFoundPage"),
);
const SupportHubPage = lazy(
  () => import("./components/Site/features/support/SupportHubPage"),
);
const SupportCategoryPage = lazy(
  () => import("./components/Site/features/support/SupportCategoryPage"),
);
const SupportArticlePage = lazy(
  () => import("./components/Site/features/support/SupportArticlePage"),
);
const Changelog = lazy(() => import("./components/Site/Changelog/Changelog"));
const FallLandingPage = lazy(
  () => import("./components/Site/features/fall/FallLandingPage"),
);
const UplandForecastPage = lazy(
  () => import("./components/Site/features/forecast/UplandForecastPage"),
);
const UplandForecastLegacyRedirect = lazy(
  () => import("./components/Site/features/forecast/UplandForecastLegacyRedirect"),
);
const PlaceDetailPage = lazy(
  () => import("./components/Site/features/places/PlaceDetailPage"),
);
const HostDashboardPage = lazy(
  () => import("./components/Site/features/host/dashboard/HostDashboardPage"),
);
const HostBookingsPage = lazy(
  () => import("./components/Site/features/host/bookings/HostBookingsPage"),
);
const HostNotificationsPage = lazy(
  () => import("./components/Site/features/host/notifications/HostNotificationsPage"),
);
const HostEventsPage = lazy(
  () => import("./components/Site/features/host/events/HostEventsPage"),
);
const HostEventEditorPage = lazy(
  () => import("./components/Site/features/host/events/HostEventEditorPage"),
);
const PlatformAdminRouteGate = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.PlatformAdminRouteGate,
  })),
);
const AdminHostEventsListPage = lazy(
  () => import("./components/Site/features/admin/AdminHostEventsListPage"),
);
const AdminHostWorkspacePage = lazy(
  () => import("./components/Site/features/admin/AdminHostWorkspacePage"),
);
const AdminHostsLayoutPage = lazy(
  () => import("./components/Site/features/admin/AdminHostsLayoutPage"),
);
const AdminHostUsersPage = lazy(
  () => import("./components/Site/features/admin/AdminHostUsersPage"),
);
const AdminMonitoringPage = lazy(
  () => import("./components/Site/features/admin/AdminMonitoringPage"),
);
const AdminBookingsPage = lazy(
  () => import("./components/Site/features/admin/AdminBookingsPage"),
);
const AdminOverviewPage = lazy(
  () => import("./components/Site/features/admin/AdminOverviewPage"),
);
const AdminFinancialsPage = lazy(
  () => import("./components/Site/features/admin/AdminFinancialsPage"),
);
const AdminDispersedCampsPage = lazy(
  () => import("./components/Site/features/admin/AdminDispersedCampsPage"),
);
const AdminSpeciesPage = lazy(
  () => import("./components/Site/features/admin/AdminSpeciesPage"),
);
const HostPayoutsPage = lazy(
  () => import("./components/Site/features/host/payouts/HostPayoutsPage"),
);
const HostComplianceProfilePage = lazy(
  () => import("./components/Site/features/host/profile/HostComplianceProfilePage"),
);
const HostPageEditor = lazy(
  () => import("./components/Site/features/host/profile/HostPageEditor"),
);
const UserProfileEditor = lazy(
  () => import("./components/Site/features/user/UserProfileEditor"),
);
/** Legacy public profile URL; canonical path is `/hosts/:slug`. */
/** Old bookmarks pointed at /host/terms; host responsibilities are only on onboarding now. */
const LegacyHostTermsToOnboardingRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/host/onboarding${search}`} replace />;
};

const LegacyPublicHostSlugRedirect: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { ready } = useEventsFeature();
  const trimmed = typeof slug === "string" ? slug.trim() : "";
  if (!trimmed) {
    if (!ready) {
      return (
        <div
          style={{
            minHeight: "40vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      );
    }
    return <Navigate to="/events" replace />;
  }
  return <Navigate to={`/hosts/${encodeURIComponent(trimmed)}`} replace />;
};

const LegacyPublicSpeciesPathRedirect: React.FC = () => {
  const location = useLocation();
  const rest = location.pathname.slice(LEGACY_PUBLIC_SPECIES_PATH_PREFIX.length);
  return (
    <Navigate
      to={{
        pathname: `${PUBLIC_FIELD_GUIDE_BASE}${rest}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

/** Public share link (`/fall-color`, `/autumn-color`) → map with the overlay on; keeps `?lat=&lng=&z=`. */
const FallColorShareRedirect: React.FC = () => {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: FALL_COLOR_MAP_PATH, search: location.search }}
      replace
    />
  );
};

const LegacyMapSpeciesPathRedirect: React.FC = () => {
  const location = useLocation();
  const rest = location.pathname.slice(LEGACY_MAP_SPECIES_PATH_PREFIX.length);
  return (
    <Navigate
      to={{
        pathname: `${MAP_FIELD_GUIDE_BASE}${rest}`,
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

const RouteFallback: React.FC<{ isMapsRoute: boolean }> = ({ isMapsRoute }) => (
  <div
    style={{
      width: "100%",
      minHeight: isMapsRoute ? "var(--app-viewport-height)" : "40vh",
      height: isMapsRoute ? "100%" : "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isMapsRoute ? "#f5f3ea" : "transparent",
    }}
  />
);

const SpeciesRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <SpeciesProvider>{children}</SpeciesProvider>;

/** Detail renders from `/api/species?slug=`; the catalog is only a fallback (WN-223). */
const SpeciesDetailRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <SpeciesProvider fetchOnMount={false}>{children}</SpeciesProvider>;

/** Full species payload so map menu species detail matches /field-guide/:category/:slug (summary omits long-form fields). */
const MapSpeciesRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <SpeciesProvider>{children}</SpeciesProvider>;

const AppUserScope = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.AppUserScope,
  })),
);
const MapUserScope = lazy(() =>
  import("./app/maps_scope").then((module) => ({
    default: module.AppUserScope,
  })),
);
const AuthPageGate = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.AuthPageGate,
  })),
);
const PendingAuthRedirectRoute = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.PendingAuthRedirectRoute,
  })),
);
const ProtectedRoute = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.ProtectedRoute,
  })),
);
const ProtectedMapRoute = lazy(() =>
  import("./app/maps_scope").then((module) => ({
    default: module.ProtectedMapRoute,
  })),
);
const VerifiedEmailRoute = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.VerifiedEmailRoute,
  })),
);
const DashboardRoute = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.DashboardRoute,
  })),
);
const HostStartRoute = lazy(() =>
  import("./app/auth_scope").then((module) => ({
    default: module.HostStartRoute,
  })),
);

const App: React.FC = () => {
  const [selectedLocation, setSelectedLocation] = useState<UserLocation | null>(
    null,
  );
  const location = useLocation();
  const navigate = useNavigate();
  useRouteSeoPolicy();

  useEffect(() => {
    const isMapsRoute = location.pathname.startsWith("/maps");

    if (isMapsRoute) {
      document.body.classList.add("maps-page");
    } else {
      document.body.classList.remove("maps-page");
    }

    return () => {
      document.body.classList.remove("maps-page");
    };
  }, [location.pathname]);

  const handleOpenLocationDetails = useCallback(
    (mapLocation: UserLocation) => {
      flushSync(() => {
        setSelectedLocation(mapLocation);
        navigate(
          userLocationMapDetailPath(mapLocation.latitude, mapLocation.longitude),
          {
            state: { location: mapLocation },
          },
        );
      });

      dispatchMapEvent(MAP_EVENTS.openLocationDetails, { location: mapLocation });
    },
    [navigate],
  );

  useEffect(() => {
    const handleClearSelectedLocationEvent = () => {
      setSelectedLocation(null);
      if (parseUserLocationMapPath(window.location.pathname)) {
        navigate("/maps", { replace: true });
      }
    };

    return subscribeMapEvent(MAP_EVENTS.clearSelectedLocation, handleClearSelectedLocationEvent);
  }, [navigate]);

  const isMapsRoute =
    location.pathname.startsWith("/maps") ||
    location.pathname.startsWith("/observation/");
  const shouldResolvePendingRedirectOnLanding =
    location.pathname === "/" && hasPendingAuthRedirectFlag();

  return (
    <div
      style={{
        width: "100%",
        ...(isMapsRoute
          ? {
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }
          : {
              minHeight: "var(--app-viewport-height)",
              height: "auto",
              display: "flex",
              flexDirection: "column",
              overflow: "visible",
            }),
      }}
    >
      <UnverifiedEmailBanner />
      <div
        style={{
          display: "flex",
          position: "relative",
          overflow: isMapsRoute ? "hidden" : "visible",
          ...(isMapsRoute
            ? { flex: 1, minHeight: 0, height: "auto" }
            : { flex: "1 1 auto", height: "auto" }),
        }}
      >
        <Suspense fallback={<RouteFallback isMapsRoute={isMapsRoute} />}>
          <MobileAuthHandoff />
          <Routes>
            <Route
              path="/"
              element={
                shouldResolvePendingRedirectOnLanding ? (
                  <PendingAuthRedirectRoute />
                ) : (
                  <SpeciesProvider>
                    <LandingPage />
                  </SpeciesProvider>
                )
              }
            />
            <Route
              path="/login"
              element={
                <AuthPageGate>
                  <LoginForm />
                </AuthPageGate>
              }
            />
            <Route
              path="/signup"
              element={
                <AuthPageGate>
                  <LoginForm />
                </AuthPageGate>
              }
            />
            <Route path="/auth/action" element={<AuthActionPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/species/*"
              element={<LegacyPublicSpeciesPathRedirect />}
            />
            <Route
              path="/field-guide/:category/:slug"
              element={
                <SpeciesDetailRoute>
                  <SpeciesDetailPageRoute />
                </SpeciesDetailRoute>
              }
            />
            {/* Single list route so /field-guide ↔ /field-guide/:category does not remount provider + page */}
            <Route
              path="/field-guide/:category?"
              element={
                <SpeciesRoute>
                  <SpeciesPage />
                </SpeciesRoute>
              }
            />
            <Route
              path="/events"
              element={
                <PublicEventsRouteGate>
                  <EventsPage />
                </PublicEventsRouteGate>
              }
            />
            <Route
              path="/events/categories/:categorySlug"
              element={
                <PublicEventsRouteGate>
                  <EventCategoryPage />
                </PublicEventsRouteGate>
              }
            />
            <Route
              path="/find"
              element={
                <PublicEventsRouteGate>
                  <GuideSearchResultsPage />
                </PublicEventsRouteGate>
              }
            />
            <Route
              path="/guides/:slugOrId"
              element={
                GUIDE_DIRECTORY_PUBLIC ? (
                  <PublicEventsRouteGate>
                    <GuideDirectoryDetailPage />
                  </PublicEventsRouteGate>
                ) : (
                  <Navigate to="/events" replace />
                )
              }
            />
            <Route
              path="/sellers/:slug"
              element={<LegacyPublicHostSlugRedirect />}
            />
            <Route path="/hosts/:slug" element={<HostPublicProfilePage />} />
            <Route
              path="/places/:gazId"
              element={
                <React.Suspense fallback={null}>
                  <PlaceDetailPage />
                </React.Suspense>
              }
            />
            <Route path="/host/start" element={<HostStartRoute />} />
            <Route
              path="/host/onboarding"
              element={
                <ProtectedRoute>
                  <VerifiedEmailRoute>
                    <AppUserScope>
                      <HostOnboardingPage />
                    </AppUserScope>
                  </VerifiedEmailRoute>
                </ProtectedRoute>
              }
            />
            <Route
              path="/host/terms"
              element={
                <ProtectedRoute>
                  <AppUserScope>
                    <LegacyHostTermsToOnboardingRedirect />
                  </AppUserScope>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events/booking-confirmed"
              element={
                <PublicEventsRouteGate>
                  <ProtectedRoute>
                    <BookingConfirmedPage />
                  </ProtectedRoute>
                </PublicEventsRouteGate>
              }
            />
            <Route
              path="/events/:slug/checkout"
              element={
                <PublicEventsRouteGate>
                  <EventCheckoutPage />
                </PublicEventsRouteGate>
              }
            />
            <Route
              path="/events/:slug"
              element={
                <PublicEventsRouteGate>
                  <EventDetailPage />
                </PublicEventsRouteGate>
              }
            />
            <Route path="/map" element={<Navigate to="/maps" replace />} />
            {[FALL_COLOR_SHARE_PATH, ...FALL_COLOR_SHARE_ALIAS_PATHS].map((path) => (
              <Route key={path} path={path} element={<FallColorShareRedirect />} />
            ))}
            <Route
              path="/maps/species/*"
              element={<LegacyMapSpeciesPathRedirect />}
            />
            <Route
              path="/maps/*"
              element={
                <ProtectedMapRoute>
                  <MapUserScope>
                    <MapSpeciesRoute>
                      <MapRouteGate
                        onOpenLocationDetails={handleOpenLocationDetails}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    </MapSpeciesRoute>
                  </MapUserScope>
                </ProtectedMapRoute>
              }
            />
            <Route
              path="/observation/:id"
              element={
                <ProtectedMapRoute>
                  <MapUserScope>
                    <MapSpeciesRoute>
                      <MapRouteGate
                        onOpenLocationDetails={handleOpenLocationDetails}
                        selectedLocation={selectedLocation}
                        setSelectedLocation={setSelectedLocation}
                      />
                    </MapSpeciesRoute>
                  </MapUserScope>
                </ProtectedMapRoute>
              }
            />
            <Route
              path="/hosting"
              element={
                <AppUserScope>
                  <HostingPage />
                </AppUserScope>
              }
            />
            <Route
              path="/membership"
              element={
                <AppUserScope>
                  <MembershipPage />
                </AppUserScope>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppUserScope>
                    <UserSettings />
                  </AppUserScope>
                </ProtectedRoute>
              }
            />
            <Route
              path="/u/:userSlug"
              element={
                <ProtectedRoute>
                  <AppUserScope>
                    <UserProfileEditor />
                  </AppUserScope>
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <AppUserScope>
                    <UserProfileEditor />
                  </AppUserScope>
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <AppUserScope>
                    <UserNotificationsPage />
                  </AppUserScope>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="/dashboard"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostDashboardPage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/events"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <DashboardEventsRouteGate>
                      <HostEventsPage />
                    </DashboardEventsRouteGate>
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/events/new"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <DashboardEventsRouteGate>
                      <HostEventEditorPage />
                    </DashboardEventsRouteGate>
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/events/:eventId"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <DashboardEventsRouteGate>
                      <HostEventEditorPage />
                    </DashboardEventsRouteGate>
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            {/* ── Admin portal — all /admin/* routes share AdminPageShell ── */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <PlatformAdminRouteGate>
                    <AppUserScope>
                      <AdminHostsLayoutPage />
                    </AppUserScope>
                  </PlatformAdminRouteGate>
                </ProtectedRoute>
              }
            >
              {/* Default: redirect /admin → /admin/overview */}
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<AdminOverviewPage />} />
              <Route path="monitoring" element={<AdminMonitoringPage />} />
              <Route path="financials" element={<AdminFinancialsPage />} />
              <Route path="dispersed-camps" element={<AdminDispersedCampsPage />} />
              <Route path="species/*" element={<AdminSpeciesPage />} />
              {/* Backward compat redirects */}
              {/* Absolute targets: a relative `to` resolves against this route's
                  own path, so "hosts/bookings" from /admin/bookings produced
                  /admin/bookings/hosts/bookings and rendered nothing. */}
              <Route path="bookings" element={<Navigate to="/admin/hosts/bookings" replace />} />
              <Route path="bookings/:bookingId" element={<LegacyAdminBookingDetailRedirect />} />
              <Route path="host-events" element={<Navigate to="/admin/hosts" replace />} />
              {/* Host management sub-tree */}
              <Route path="hosts">
                <Route index element={<AdminHostEventsListPage />} />
                <Route path="users" element={<AdminHostUsersPage />} />
                <Route path="bookings" element={<AdminBookingsPage />} />
                <Route path="bookings/:bookingId" element={<AdminBookingsPage />} />
                <Route path=":hostId" element={<AdminHostWorkspacePage />} />
              </Route>
            </Route>
            {/* The platform-admin listing editor was removed; old links land on
                the host's workspace. */}
            <Route
              path="/admin/host-events/:eventId"
              element={<Navigate to="/admin/hosts" replace />}
            />
            <Route
              path="/dashboard/bookings"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostBookingsPage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/bookings/:bookingId"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostBookingsPage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/notifications"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostNotificationsPage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/host/compliance"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostComplianceProfilePage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/payouts"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostPayoutsPage />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/page"
              element={
                <DashboardRoute>
                  <AppUserScope>
                    <HostPageEditor />
                  </AppUserScope>
                </DashboardRoute>
              }
            />
            <Route
              path="/dashboard/profile"
              element={<Navigate to="/profile" replace />}
            />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/support/:category/:article"
              element={<SupportArticlePage />}
            />
            <Route
              path="/support/:category"
              element={<SupportCategoryPage />}
            />
            <Route path="/support" element={<SupportHubPage />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/fall" element={<FallLandingPage />} />
            {/* Also a 301 in vercel.json; this covers client-side navigation. */}
            <Route path="/autumn" element={<Navigate to="/fall" replace />} />
            {/* Old forecast hub: the upland page is the only forecast route now. */}
            <Route
              path="/forecast"
              element={<Navigate to="/forecast/upland" replace />}
            />
            <Route
              path="/forecast/upland/:year?"
              element={
                <PublicForecastRouteGate>
                  <UplandForecastPage />
                </PublicForecastRouteGate>
              }
            />
            <Route
              path="/forecasts/upland/:year?"
              element={
                <PublicForecastRouteGate>
                  <UplandForecastLegacyRedirect />
                </PublicForecastRouteGate>
              }
            />
            <Route
              path="/forecasts/*"
              element={
                <PublicForecastRouteGate>
                  <Navigate to="/forecast/upland" replace />
                </PublicForecastRouteGate>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
      <DeferredAnalytics />
    </div>
  );
};

export default App;
